import React, { useState } from 'react';
import {
    Table,
    Card,
    Row,
    Col,
    Input,
    Button,
    Space,
    Tag,
    Typography,
    Divider,
    message,
    Form,
    Select,
    Badge,
    Drawer,
    Radio,
    Descriptions
} from 'antd';
import {
    CheckCircleOutlined,
    AppleOutlined,
    AndroidOutlined,
    InfoCircleOutlined,
    CreditCardOutlined,
    ScheduleOutlined,
    FileTextOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const AppAudit = () => {
    const [loading] = useState(false);
    const [searchForm] = Form.useForm();
    const [auditDrawerVisible, setAuditDrawerVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [auditForm] = Form.useForm();

    // 为了让 Drawer 能正确感知 status 的变化，我们使用 antd 的 Form.useWatch
    const auditStatus = Form.useWatch('status', auditForm);

    // 模拟审核数据
    const dataSource = [
        {
            key: '1',
            appName: '模板app_3',
            platform: 'iOS',
            region: 'mainland',
            bundleId: 'com.hm.template3',
            payType: 'platform',
            payChannels: ['微信', '支付宝', 'Apple Pay'],
            status: 'pending',
            enterpriseName: '张三科技',
            enterpriseId: 'E827192',
            createTime: '2026-03-11 10:15:18',
        },
        {
            key: '2',
            appName: '模板app_2',
            platform: 'Android',
            region: 'overseas',
            bundleId: 'com.hm.template2',
            payType: 'firm',
            payChannels: ['微信', '支付宝'],
            status: 'pending',
            enterpriseName: '李四实业',
            enterpriseId: 'E992301',
            createTime: '2026-03-10 14:20:05',
        },
    ];

    const handleAudit = (record) => {
        setCurrentRecord(record);
        setAuditDrawerVisible(true);
        auditForm.setFieldsValue({ status: 'pass', operationMode: 'self' });
    };

    const submitAudit = () => {
        auditForm.validateFields().then(values => {
            const { status, operationMode } = values;
            const modeText = status === 'pass'
                ? `「${operationMode === 'platform' ? '平台代运营' : '企业自运营'}」`
                : '';
            message.success(status === 'pass' ? `审核通过，运营方式：${modeText}` : '已成功驳回');
            setAuditDrawerVisible(false);
            auditForm.resetFields();
        });
    };

    const columns = [
        {
            title: '序号',
            dataIndex: 'key',
            key: 'key',
            width: 70,
            align: 'center',
        },
        {
            title: '应用基本信息',
            dataIndex: 'appName',
            key: 'appName',
            render: (text) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Text strong>{text}</Text>
                </div>
            ),
        },
        {
            title: '收款方式',
            dataIndex: 'payType',
            key: 'payType',
            render: (text) => text === 'platform' ? '平台代收款' : '企业自收款'
        },
        {
            title: '申请平台',
            dataIndex: 'platform',
            key: 'platform',
            render: (text) => (
                <Space>
                    {text === 'iOS' ? <AppleOutlined /> : <AndroidOutlined />}
                    {text}
                </Space>
            )
        },
        {
            title: 'AppID',
            dataIndex: 'bundleId',
            key: 'bundleId',
        },
        {
            title: '收款通道',
            dataIndex: 'payChannels',
            key: 'payChannels',
            width: 140,
            render: (channels) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {channels?.map(channel => (
                        <Tag key={channel} color="default" style={{ margin: 0, fontSize: '11px' }}>{channel}</Tag>
                    ))}
                </div>
            )
        },
        {
            title: '上架区域',
            dataIndex: 'region',
            key: 'region',
            render: (text) => {
                const map = { mainland: '国内', global: '全球', overseas: '非大陆' };
                return map[text] || text;
            }
        },
        {
            title: '所属企业',
            dataIndex: 'enterpriseName',
            key: 'enterpriseName',
            render: (text, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Text strong>{text}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.enterpriseId}</Text>
                </div>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const map = {
                    pending: { status: 'warning', text: '待审核' },
                    pass: { status: 'success', text: '已通过' },
                    reject: { status: 'error', text: '已驳回' },
                    draft: { status: 'default', text: '待提交' },
                };
                const config = map[status] || { status: 'default', text: status };
                return <Badge status={config.status} text={config.text} />;
            }
        },
        {
            title: '申请时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
            // sorter: (a, b) => new Date(a.createTime) - new Date(b.createTime),
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <Space split={<Divider type="vertical" />}>
                    <Button 
                        type="link" 
                        size="small" 
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleAudit(record)}
                    >
                        审核
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', backgroundColor: '#fff', minHeight: '100vh' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>待审核App</Title>
      </div>
            <Card bordered={false} style={{ marginBottom: 16 }}>
                <Form form={searchForm} layout="horizontal" initialValues={{ enterpriseSearchType: 'enterpriseName', appSearchType: 'appName' }}>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Form.Item name="appSearchType" noStyle>
                                    <Select style={{ width: '120px' }}>
                                        <Option value="appName">应用名称</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="appKeyword" noStyle>
                                    <Input style={{ flex: 1 }} placeholder="请输入关键字" allowClear />
                                </Form.Item>
                            </Input.Group>
                        </Col>
                        <Col span={8}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Form.Item name="enterpriseSearchType" noStyle>
                                    <Select style={{ width: '120px' }}>
                                        <Option value="enterpriseName">企业名称</Option>
                                        <Option value="enterpriseId">企业ID</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="enterpriseKeyword" noStyle>
                                    <Input style={{ flex: 1 }} placeholder="请输入关键字" allowClear />
                                </Form.Item>
                            </Input.Group>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="系统平台" name="platform" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: '100%' }}>
                                    <Option value="Android">Android</Option>
                                    <Option value="iOS">iOS</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Space size="middle">
                                <Button type="primary" onClick={() => message.info('搜索中...')}>查询</Button>
                                <Button onClick={() => searchForm.resetFields()}>重置</Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                rowKey="key"
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条记录`,
                }}
                style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
            />

        <Drawer
                title={
                    <Space>
                        <ScheduleOutlined style={{ color: '#1890ff' }} />
                        <span>平台配置审核</span>
                    </Space>
                }
                placement="right"
                width={560}
                onClose={() => setAuditDrawerVisible(false)}
                open={auditDrawerVisible}
                footer={
                    <div style={{ textAlign: 'right', padding: '16px 24px' }}>
                        <Button onClick={() => setAuditDrawerVisible(false)} style={{ marginRight: 8 }}>
                            取消
                        </Button>
                        <Button type="primary" onClick={submitAudit}>
                            确定
                        </Button>
                    </div>
                }
                footerStyle={{ padding: 0 }}
                bodyStyle={{ padding: '0 24px 24px 24px' }}
            >
                {currentRecord && (
                    <Form form={auditForm} layout="vertical">
                        <Divider orientation="left" plain>
                            <Space><InfoCircleOutlined />基本信息</Space>
                        </Divider>
                        <Descriptions column={2} size="small" layout="vertical" bordered={false}>
                            <Descriptions.Item label="应用名称">
                                <Text strong>{currentRecord.appName}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="系统平台">
                                <Space>
                                    {currentRecord.platform === 'iOS' ? <AppleOutlined /> : <AndroidOutlined />}
                                    {currentRecord.platform}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="AppID (Bundle ID)" span={2}>
                                <Text code>{currentRecord.bundleId}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="上架区域">
                                {currentRecord.region === 'mainland' ? '国内通用' : '全球通用'}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">
                                {currentRecord.createTime}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" plain>
                            <Space><CreditCardOutlined />支付配置</Space>
                        </Divider>
                        <Descriptions column={1} size="small" layout="vertical">
                            <Descriptions.Item label="收款方式">
                                {currentRecord.payType === 'platform' ? '平台代收款' : '企业自收款'}
                            </Descriptions.Item>
                            <Descriptions.Item label="收款通道">
                                <Space wrap>
                                    {currentRecord.payChannels?.map(channel => (
                                        <Tag key={channel} color="blue">{channel}</Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" plain>
                            <Space><FileTextOutlined />审核处理</Space>
                        </Divider>
                        
                        <Card 
                            size="small" 
                            style={{ 
                                background: auditStatus === 'reject' ? '#fff1f0' : '#f6ffed',
                                border: `1px solid ${auditStatus === 'reject' ? '#ffa39e' : '#b7eb8f'}`,
                                borderRadius: '8px'
                            }}
                        >
                            <Form.Item label="审核结果" name="status" style={{ marginBottom: 12 }}>
                                <Radio.Group buttonStyle="solid">
                                    <Radio.Button value="pass">通过</Radio.Button>
                                    <Radio.Button value="reject">驳回</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {auditStatus === 'pass' && (
                                <Form.Item label="运营方式" name="operationMode" initialValue="self" style={{ marginBottom: 12 }}>
                                    <Radio.Group>
                                        <Radio value="self">企业自运营</Radio>
                                        <Radio value="platform">平台代运营</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            )}

                            {auditStatus === 'reject' ? (
                                <Form.Item 
                                    label={<span style={{ color: '#cf1322' }}>驳回原因</span>}
                                    name="remark"
                                    rules={[{ required: true, message: '请填写驳回原因' }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input.TextArea 
                                        rows={4} 
                                        placeholder="请详细描述驳回原因，以便企业快速修改..." 
                                        maxLength={200}
                                        showCount
                                    />
                                </Form.Item>
                            ) : (
                                <Form.Item 
                                    label="审核备注" 
                                    name="remark"
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input.TextArea 
                                        rows={2} 
                                        placeholder="可选，输入审核批注..." 
                                    />
                                </Form.Item>
                            )}
                        </Card>
                    </Form>
                )}
            </Drawer>
        </div>
    );
};

export default AppAudit;
